import type { PayloadAction } from "@reduxjs/toolkit";
import type { NavigateFunction } from "react-router-dom";
import type { CallEffect, PutEffect } from "redux-saga/effects";
import { call, put, takeLatest } from "redux-saga/effects";

import api from "src/services/playoutApi";

import { NOTIFY } from "../reducers/notification";
import {
	actions,
	REQUEST_CREATE_OUTPUT,
	REQUEST_DELETE_OUTPUT,
	REQUEST_GET_OUTPUTS,
	REQUEST_UPDATE_OUTPUT,
} from "../reducers/output";

type OutputApiResponse = void | { data: Output; status: number };
function* createOutput(
	action: PayloadAction<{ data: Partial<Output>; navigate?: NavigateFunction }>,
): Generator<CallEffect | PutEffect, void, OutputApiResponse> {
	try {
		const { navigate, data } = action.payload;
		const response = yield call(api.post, `/outputs`, data);

		if (response?.data) {
			yield put(actions.receiveCreateOutput());
			navigate && navigate("/settings/workspace/outputs");
			yield put({
				type: NOTIFY,
				payload: {
					variant: "success",
					title: "Output created",
				},
			});
		}
	} catch (error) {
		const apiError = error as Error;
		yield put({
			type: NOTIFY,
			payload: {
				variant: "error",
				title: apiError.name,
				description: apiError.message,
			},
		});
		yield put(
			actions.failedCreateOutput(((error as Error)?.message as string) || "Something went wrong"),
		);
	}
}

function* deleteOutput({
	payload,
}: PayloadAction<{ id: string }>): Generator<CallEffect | PutEffect, void> {
	try {
		const { id } = payload;
		const response = yield call(api.delete, `/outputs/${id}`);
		const apiResponse = response as OutputApiResponse;
		if (apiResponse?.status === 204) {
			yield put(actions.receiveDeleteOutput(id));

			yield put({
				type: NOTIFY,
				payload: {
					variant: "info",
					title: "Output deleted",
				},
			});
		}
	} catch (error) {
		const apiError = error as Error;
		yield put({
			type: NOTIFY,
			payload: {
				variant: "error",
				title: apiError.name,
				description: apiError.message,
			},
		});
		yield put(
			actions.failedDeleteOutput(((error as Error)?.message as string) || "Something went wrong"),
		);
	}
}

// The Playout Service returns { uuid, name, channel } and paginates the list
// as { meta, data }. We normalize `uuid` to `id` so existing consumers (which
// key outputs by `id`, including rundown-canvas outputs) keep working.
type PlayoutOutput = { uuid: string; name: string; channel: string };
type PaginatedOutputs = { meta: unknown; data: PlayoutOutput[] };
type OutputsApiResponse = void | { data: PaginatedOutputs; status: number };

const toOutput = ({ uuid, name, channel }: PlayoutOutput): Output => ({
	id: uuid,
	name,
	channel,
});

function* getOutputs(): Generator<
	PutEffect | CallEffect<OutputsApiResponse>,
	void,
	OutputsApiResponse
> {
	try {
		// Request a high limit to fetch all outputs in one page (there is no
		// pagination UI for outputs; workspaces have a small number of them).
		const response = yield call(api.get, `/outputs`, { params: { limit: 100 } });
		const apiResponse = response as OutputsApiResponse;

		if (apiResponse?.data?.data) {
			yield put(actions.receiveGetOutputs(apiResponse.data.data.map(toOutput)));
		}
	} catch (error) {
		yield put(
			actions.failedGetOutputs(((error as Error)?.message as string) || "Something went wrong"),
		);
	}
}

function* updateOutput({
	payload,
}: PayloadAction<{ id: string; data: Partial<Output>; navigate?: NavigateFunction }>): Generator<
	CallEffect | PutEffect,
	void,
	OutputApiResponse
> {
	try {
		const { data, id, navigate } = payload;
		const response = yield call(api.patch, `/outputs/${id}`, data);

		if (response?.data) {
			yield put(actions.receiveUpdateOutput());
			yield put(actions.requestGetOutputs());

			navigate && navigate("/settings/workspace/outputs");
			yield put({
				type: NOTIFY,
				payload: {
					variant: "success",
					title: "Output updated",
				},
			});
		}
	} catch (error) {
		const apiError = error as Error;
		yield put({
			type: NOTIFY,
			payload: {
				variant: "error",
				title: apiError.name,
				description: apiError.message,
			},
		});
		yield put(
			actions.failedUpdateOutput(((error as Error)?.message as string) || "Something went wrong"),
		);
	}
}

export default function* root() {
	yield takeLatest(REQUEST_CREATE_OUTPUT, createOutput);
	yield takeLatest(REQUEST_DELETE_OUTPUT, deleteOutput);
	yield takeLatest(REQUEST_GET_OUTPUTS, getOutputs);
	yield takeLatest(REQUEST_UPDATE_OUTPUT, updateOutput);
}
